import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';

import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';
import type { ApiErrorDetail, CreateOrder, Order, OrderType } from '@klareospace/pharos-lib-types';

import { useCreateOrder } from '&/features/orders/hooks';
import { telemetryClient } from '&/lib/telemetry';

import {
  CONTENT_SECTION_ID,
  CUSTOMER_SECTION_ID,
  GENERAL_INFORMATION_SECTION_ID,
  ROUTE_INFORMATION_SECTION_ID,
  SCHEDULING_SECTION_ID,
} from '../constants';
import type { OrderCreationSectionId } from '../constants';
import type { OrderCreationFormInput } from '../schemas/order-creation.schema';
import {
  buildCreateOrderBody,
  isCrossLegTimeWindowInverted,
  isSchedulingComplete,
  isSchedulingTimeSlotInverted,
  resolveOrderCreationErrorMapping,
} from '../utils';
import type { ShipperOptionsStatus } from './use-shipper-options';

// ─── Constants ────────────────────────────────────────────────────────────────

/** `trackException` source dimension for an error that never reached the mutation (e.g. `buildCreateOrderBody` throwing). */
const SUBMIT_ERROR_TELEMETRY_SOURCE = 'order-creation-submit';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSubmitOrderOptions {
  /** Whether every `ORDER_CREATION_SECTION_IDS` entry is in the stepper's completed list. */
  allSectionsComplete: boolean;
  form: UseFormReturn<OrderCreationFormInput>;
  /** Called once the order is created, after the success toast — the drawer resets and closes. */
  onSuccess: () => void;
  orderTypes: OrderType[];
  /** From `useShipperOptions` — the stamped `shipperCode` itself comes from the form, not from here. */
  shipperOptionsStatus: ShipperOptionsStatus;
  /** Marks a section incomplete and opens it. */
  uncompleteSection: (id: OrderCreationSectionId) => void;
  validateContent: () => Promise<boolean>;
  validateCustomer: () => Promise<boolean>;
  validateGeneralInformation: () => Promise<boolean>;
  validateRouteInformation: () => Promise<boolean>;
}

export interface UseSubmitOrderReturn {
  /** `allSectionsComplete && shipperOptionsStatus === 'ready' && !isSubmitting` — owned here so the drawer stays presentational. */
  canSubmit: boolean;
  handleSubmit: () => Promise<void>;
  /** Covers both section re-validation and the mutation itself — not just `createOrder.isPending`. */
  isSubmitting: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Owns the manual order creation submit flow: re-validates sections, builds the payload, submits, and maps server errors back onto the right section. */
export function useSubmitOrder({
  allSectionsComplete,
  form,
  onSuccess,
  orderTypes,
  shipperOptionsStatus,
  uncompleteSection,
  validateContent,
  validateCustomer,
  validateGeneralInformation,
  validateRouteInformation,
}: UseSubmitOrderOptions): UseSubmitOrderReturn {
  const { t } = useTranslation('order-creation');
  const createOrder = useCreateOrder();
  // Synchronous guard against a double-click sending two `POST /orders` — `createOrder.isPending`
  // only flips once the mutation starts, which is after the async section re-validation below.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function validateAllSections(): Promise<boolean> {
    const validators: [OrderCreationSectionId, () => Promise<boolean>][] = [
      [GENERAL_INFORMATION_SECTION_ID, validateGeneralInformation],
      [CUSTOMER_SECTION_ID, validateCustomer],
      [ROUTE_INFORMATION_SECTION_ID, validateRouteInformation],
      [CONTENT_SECTION_ID, validateContent],
    ];
    const results = await Promise.all(validators.map(([, validate]) => validate()));
    const failingIndex = results.findIndex((result) => !result);
    if (failingIndex === -1) return true;
    uncompleteSection(validators[failingIndex][0]);
    return false;
  }

  /** Scheduling has no `validate*` function (it auto-completes live) — re-checked here for staleness and the cross-leg guard. */
  function validateSchedulingForSubmit(): boolean {
    const scheduling = form.getValues('scheduling');
    if (!isSchedulingComplete(scheduling)) {
      if (isSchedulingTimeSlotInverted(scheduling)) {
        form.setError('scheduling', { message: t('sections.scheduling.invalidRange'), type: 'manual' });
      }
      uncompleteSection(SCHEDULING_SECTION_ID);
      return false;
    }

    const pickupTimeSlot = form.getValues('routeInformation.pickupTimeSlot');
    if (isCrossLegTimeWindowInverted(pickupTimeSlot, scheduling.deliveryTimeSlot)) {
      form.setError('scheduling', { message: t('submit.crossLegTimeWindow'), type: 'manual' });
      uncompleteSection(SCHEDULING_SECTION_ID);
      return false;
    }
    return true;
  }

  /** Maps the rejection to its field/section/message handling and shows the matching error toast. */
  function handleSubmitError(detail: ApiErrorDetail): void {
    const mapping = resolveOrderCreationErrorMapping(detail);
    const message = t(mapping.messageKey);
    if (mapping.field) form.setError(mapping.field, { message, type: 'manual' });
    if (mapping.section) uncompleteSection(mapping.section);
    toast.error(t('submit.error'), { description: message });
  }

  // `detail` is extracted once by `createMutationCallbacks` itself — this hook never calls
  // `getApiErrorDetail` directly, so a rejection is never parsed twice.
  const { onError: handleCreateOrderError } = createMutationCallbacks<Order, CreateOrder>({
    onError: (detail) => handleSubmitError(detail),
  });

  /** Already toasted/field-mapped via `handleCreateOrderError` — swallowed so the outer catch in `handleSubmit` doesn't show a second, generic toast on top. */
  async function submitToApi(body: CreateOrder): Promise<void> {
    try {
      const order = await createOrder.mutateAsync(body, { onError: handleCreateOrderError });
      toast.success(t('submit.success'), {
        description: t('submit.successDescription', { referenceNumber: order.referenceNumber }),
      });
      onSuccess();
    } catch {
      // handled above
    }
  }

  async function handleSubmit(): Promise<void> {
    if (isSubmittingRef.current || shipperOptionsStatus !== 'ready') return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const sectionsValid = await validateAllSections();
      if (!sectionsValid) return;
      if (!validateSchedulingForSubmit()) return;

      const body = buildCreateOrderBody(form.getValues(), { orderTypes });
      await submitToApi(body);
    } catch (error) {
      // Only reached for an error that never made it to the mutation (e.g. `buildCreateOrderBody`
      // throwing) — mutation rejections are already handled by `submitToApi`. Reported so it isn't
      // silently swallowed — the error itself carries no customer/order data (no PII).
      telemetryClient.trackException(error, { source: SUBMIT_ERROR_TELEMETRY_SOURCE });
      toast.error(t('submit.error'), { description: t('submit.errorDescription') });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  const canSubmit = allSectionsComplete && shipperOptionsStatus === 'ready' && !isSubmitting;

  return { canSubmit, handleSubmit, isSubmitting };
}
